import React from 'react';
import { X, Minus, Plus, AlignLeft, AlignJustify } from 'lucide-react';
import {
  READER_RANGES, THEME_LABELS, type ReaderSettings, type ReaderTheme,
} from './readerSettings';
import './styles/readerSettings.css';

interface ReaderSettingsPanelProps {
  settings: ReaderSettings;
  onChange: (patch: Partial<ReaderSettings>) => void;
  onReset: () => void;
  onClose: () => void;
}

const THEME_ORDER: ReaderTheme[] = ['light', 'sepia', 'eye', 'dark'];

/**
 * 阅读排版设置面板。
 * 字号/行距/宽度/内边距/缩进/主题均可调，改动实时生效并持久化。
 */
export function ReaderSettingsPanel({ settings, onChange, onReset, onClose }: ReaderSettingsPanelProps) {
  const stepper = (
    label: string,
    key: 'fontSize' | 'lineHeight' | 'contentWidth' | 'pagePadding',
    format: (v: number) => string,
  ) => {
    const range = READER_RANGES[key];
    const value = settings[key];
    const atMin = value <= range.min;
    const atMax = value >= range.max;
    const step = (delta: number) => {
      // 行高是浮点，直接用加减会累积误差，故按步长取整到 1 位小数
      const next = Number((value + delta * range.step).toFixed(1));
      onChange({ [key]: next } as Partial<ReaderSettings>);
    };
    return (
      <div className="rs-row" key={key}>
        <span className="rs-label">{label}</span>
        <div className="rs-stepper">
          <button className="rs-step-btn" onClick={() => step(-1)} disabled={atMin} aria-label={`减小${label}`}>
            <Minus size={13} />
          </button>
          <span className="rs-value">{format(value)}</span>
          <button className="rs-step-btn" onClick={() => step(1)} disabled={atMax} aria-label={`增大${label}`}>
            <Plus size={13} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="rs-panel" role="dialog" aria-label="阅读设置">
      <div className="rs-head">
        <strong>阅读设置</strong>
        <button className="rs-close" onClick={onClose} aria-label="关闭阅读设置" title="关闭">
          <X size={14} />
        </button>
      </div>

      <div className="rs-row">
        <span className="rs-label">主题</span>
        <div className="rs-themes">
          {THEME_ORDER.map((t) => (
            <button
              key={t}
              className={`rs-theme rs-theme-${t}${settings.theme === t ? ' is-active' : ''}`}
              onClick={() => onChange({ theme: t })}
              aria-pressed={settings.theme === t}
              title={THEME_LABELS[t]}
            >
              <span className="rs-theme-swatch" data-theme={t} />
              {THEME_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      {stepper('字号', 'fontSize', (v) => `${v}px`)}
      {stepper('行距', 'lineHeight', (v) => v.toFixed(1))}
      {stepper('页宽', 'contentWidth', (v) => `${v}px`)}
      {stepper('边距', 'pagePadding', (v) => `${v}px`)}

      <div className="rs-row">
        <span className="rs-label">首行缩进</span>
        <div className="rs-toggle-group">
          <button
            className={`rs-toggle${settings.indent ? ' is-active' : ''}`}
            onClick={() => onChange({ indent: true })}
            aria-pressed={settings.indent}
            title="首行缩进 2 字符（中文小说常见排版）"
          >
            <AlignJustify size={13} /> 开
          </button>
          <button
            className={`rs-toggle${!settings.indent ? ' is-active' : ''}`}
            onClick={() => onChange({ indent: false })}
            aria-pressed={!settings.indent}
            title="不缩进"
          >
            <AlignLeft size={13} /> 关
          </button>
        </div>
      </div>

      <div className="rs-foot">
        <button className="rs-reset" onClick={onReset}>恢复默认</button>
      </div>
    </div>
  );
}
